<template>
  <div class="form-stack text-left">
    <section class="donor-form-panel">
      <p class="form-section-title">Profile picture</p>
      <div class="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
        <div class="relative shrink-0">
          <img
            v-if="fundraiser.profilePicturePreview"
            :src="fundraiser.profilePicturePreview"
            alt="Profile preview"
            class="h-24 w-24 rounded-full object-cover ring-2 ring-primary/30"
          />
          <img
            v-else-if="fundraiser.currentProfilePicture"
            :src="fundraiser.currentProfilePicture"
            alt="Current profile"
            class="h-24 w-24 rounded-full object-cover ring-2 ring-gray-200"
          />
          <div
            v-else
            class="flex h-24 w-24 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primaryDark ring-2 ring-gray-200"
          >
            {{ fundraiser.firstName?.[0]?.toUpperCase() || '?' }}
          </div>
          <button
            v-if="fundraiser.profilePicturePreview || fundraiser.currentProfilePicture"
            type="button"
            class="absolute -right-1 -top-1 rounded-full bg-red-500 p-1 text-white shadow-sm transition-colors hover:bg-red-600"
            aria-label="Remove profile picture"
            @click="$emit('remove-profile-picture')"
          >
            <XMarkIcon class="h-4 w-4" />
          </button>
        </div>
        <div class="w-full min-w-0 flex-1">
          <label for="edit-profile-picture" class="input--label--small">Upload new photo</label>
          <input
            id="edit-profile-picture"
            type="file"
            accept="image/*"
            class="mt-1 block w-full text-sm text-gray-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-primaryDark hover:file:bg-primary/20"
            @change="$emit('file-change', $event)"
          />
          <p class="mt-1 text-xs text-gray-500">
            JPG or PNG. Leave empty to keep the current photo.
          </p>
        </div>
      </div>
    </section>

    <section class="form-section">
      <p class="form-section-title">Personal details</p>
      <div class="form-grid">
        <div>
          <label for="edit-firstName" class="input--label--small">First name</label>
          <input
            id="edit-firstName"
            :value="fundraiser.firstName"
            type="text"
            class="input--text--small mt-1 w-full"
            placeholder="First name"
            @input="updateField('firstName', $event.target.value)"
          />
        </div>
        <div>
          <label for="edit-lastName" class="input--label--small">Last name</label>
          <input
            id="edit-lastName"
            :value="fundraiser.lastName"
            type="text"
            class="input--text--small mt-1 w-full"
            placeholder="Last name"
            @input="updateField('lastName', $event.target.value)"
          />
        </div>
        <div class="form-field-full">
          <label for="edit-email" class="input--label--small">Email</label>
          <input
            id="edit-email"
            :value="fundraiser.email"
            type="email"
            class="input--text--small mt-1 w-full"
            placeholder="example@email.com"
            autocomplete="email"
            @input="updateField('email', $event.target.value)"
          />
        </div>
        <div>
          <label for="edit-employeeId" class="input--label--small">Employee ID</label>
          <input
            id="edit-employeeId"
            :value="fundraiser.employeeId"
            type="text"
            class="input--text--small mt-1 w-full"
            placeholder="Employee ID"
            @input="updateField('employeeId', $event.target.value)"
            @blur="$emit('validate-employee-id', fundraiser.employeeId)"
          />
          <p v-if="employeeIdError" class="input--error">{{ employeeIdError }}</p>
        </div>
      </div>
    </section>

    <section class="form-section form-section-divider">
      <p class="form-section-title">Security</p>
      <div>
        <label for="edit-password" class="input--label--small">New password</label>
        <input
          id="edit-password"
          :value="fundraiser.password"
          type="password"
          class="input--text--small mt-1 w-full"
          placeholder="Leave blank to keep current password"
          autocomplete="new-password"
          @input="updateField('password', $event.target.value)"
        />
        <p class="mt-1 text-xs text-gray-500">
          Only fill in if you want to reset this user's password.
        </p>
      </div>
    </section>

    <section class="form-section form-section-divider">
      <p class="form-section-title">Organization</p>
      <div class="form-grid">
        <div class="form-field-full">
          <label for="edit-homeOffice" class="input--label--small">Home office</label>
          <HomeOfficeSelect
            input-id="edit-homeOffice"
            :model-value="fundraiser.homeOfficeId"
            placeholder="Select home office"
            input-class="mt-1 block w-full"
            :initial-options="homeOfficesList"
            :initial-has-more="homeOfficesHasMore"
            @update:model-value="updateField('homeOfficeId', $event)"
          />
        </div>
        <div v-if="canChangeProfileType" class="form-field-full">
          <label for="edit-profileType" class="input--label--small">Profile type</label>
          <select
            id="edit-profileType"
            :value="fundraiser.profileType"
            class="input--text--small mt-1 w-full"
            :disabled="
              hasActiveCampaigns && fundraiser.profileType === 'Canvassing Org Field Director'
            "
            @change="updateField('profileType', $event.target.value)"
          >
            <option value="Fundraiser">Fundraiser</option>
            <option value="Canvassing Org Field Director" :disabled="hasActiveCampaigns">
              Field Director
            </option>
          </select>
          <p v-if="hasActiveCampaigns" class="mt-1 text-xs text-amber-600">
            Cannot change to Field Director while actively assigned to campaigns.
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { XMarkIcon } from '@heroicons/vue/24/outline'
import HomeOfficeSelect from '@/components/HomeOfficeSelect.vue'

export default {
  name: 'FundraiserEditForm',
  components: {
    HomeOfficeSelect,
    XMarkIcon,
  },
  props: {
    fundraiser: {
      type: Object,
      required: true,
    },
    employeeIdError: {
      type: String,
      default: '',
    },
    canChangeProfileType: {
      type: Boolean,
      default: false,
    },
    hasActiveCampaigns: {
      type: Boolean,
      default: false,
    },
    homeOfficesList: {
      type: Array,
      default: () => [],
    },
    homeOfficesHasMore: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['remove-profile-picture', 'file-change', 'validate-employee-id', 'update-field'],
  methods: {
    updateField(key, value) {
      this.$emit('update-field', { key, value })
    },
  },
}
</script>
